/**
 * Frontend dispatch helper.
 *
 * Phase 11 — wraps `fetch` for `POST /api/dispatch/:action`. Generates
 * a UUID v7 `request_id` if not supplied; appends the bearer JWT from
 * the Supabase session; resolves to a discriminated `DispatchResult`
 * (NOT throw on dispatch failure — only throw on network failure).
 *
 * Per the phase 11 locked sub-decisions:
 *
 *   - `request_id` is client-generated UUID v7. Re-running the same
 *     form mints a FRESH `request_id` so manual retries land as new
 *     audit_log rows (not auto-replays). Replay is opt-in: the caller
 *     supplies an explicit `requestId`.
 *   - `correlation_id` is OPTIONAL on the wire. The backend
 *     synthesises `dashboard:<staff_id>:<request_id>` when absent.
 *   - Reply shape: `{ok: true, outcome: 'ok', response, idempotency_replay_short_circuit?}`
 *     OR `{ok: false, code, message}`. NEVER includes a stack trace.
 */

import {
  bearerToken,
  isCapabilityView,
  SANDBOX_READ_ONLY_CODE,
  SANDBOX_READ_ONLY_MESSAGE,
  DEMO_STAFF_STANDIN,
} from "./capability";

export interface DispatchOptions {
  readonly requestId?: string;
  readonly correlationId?: string;
}

export interface DispatchSuccess<TOutput> {
  readonly ok: true;
  readonly outcome: "ok";
  readonly response: TOutput;
  readonly idempotency_replay_short_circuit?: boolean;
  /** Echoes the `request_id` the SPA sent — so callers can display it. */
  readonly request_id: string;
}

export interface DispatchFailure {
  readonly ok: false;
  readonly code: string;
  readonly message: string;
  readonly request_id: string;
}

export type DispatchResult<TOutput> =
  | DispatchSuccess<TOutput>
  | DispatchFailure;

/**
 * Generate a UUID v7. `crypto.randomUUID()` returns v4; phase 11
 * brief locks v7 (monotonic, sortable). Until a shared helper lands
 * in `@layer/canonical-data`, we inline a minimal v7 generator that
 * matches the regex enforced by the action input schemas.
 */
export function generateRequestIdV7(): string {
  const ts = Date.now();
  const bytes = new Uint8Array(16);
  bytes[0] = (ts / 2 ** 40) & 0xff;
  bytes[1] = (ts / 2 ** 32) & 0xff;
  bytes[2] = (ts / 2 ** 24) & 0xff;
  bytes[3] = (ts / 2 ** 16) & 0xff;
  bytes[4] = (ts / 2 ** 8) & 0xff;
  bytes[5] = ts & 0xff;
  // Use crypto.getRandomValues when available (browser); fall back to
  // Math.random for tests without DOM.
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    const rand = new Uint8Array(10);
    crypto.getRandomValues(rand);
    for (let i = 6; i < 16; i += 1) {
      bytes[i] = rand[i - 6] ?? 0;
    }
  } else {
    for (let i = 6; i < 16; i += 1) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  // Set version (7) and variant (10).
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x70;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * Dispatch a canonical action through the dashboard backend.
 *
 * @throws Error on network failure or a non-JSON response. Dispatch
 * failures (DispatchError mapped to HTTP 4xx/5xx) resolve to
 * `{ok: false, code, message}` — callers render an error block.
 */
export async function dispatchAction<TInput, TOutput>(
  actionName: string,
  input: TInput,
  options?: DispatchOptions,
): Promise<DispatchResult<TOutput>> {
  const requestId = options?.requestId ?? generateRequestIdV7();

  /* A capability view is read-only, and says so BEFORE the network.
   *
   * `/member-view/:token` authorises one member's reads and nothing else, so
   * every write here would 401 — and it did, showing
   * "HTTP 401 returned no message (whoami_unauthorized)" inside the Clavis
   * demo, where the framed dashboard is real and so are its Book / Buy /
   * Refund / Edit controls.
   *
   * Declining here rather than at the server is deliberate on both counts: the
   * visitor gets a sentence instead of a status code, and the request never
   * leaves the browser, so the guard costs nothing and cannot be mistaken for
   * an outage. The SERVER still refuses these writes independently — this is
   * the wording, never the protection. */
  //
  // Skipped on the Clavis demo, where the server accepts this very token as a
  // staff stand-in and the writes genuinely land. This guard is the wording,
  // not the protection — so where there is nothing to decline, declining here
  // would be the ONLY thing stopping a booking or a refund, and the buttons
  // would go on looking live while doing nothing. See DEMO_STAFF_STANDIN.
  if (!DEMO_STAFF_STANDIN && isCapabilityView()) {
    return {
      ok: false,
      code: SANDBOX_READ_ONLY_CODE,
      message: SANDBOX_READ_ONLY_MESSAGE,
      request_id: requestId,
    };
  }

  const token = await bearerToken();
  const body = {
    input,
    request_id: requestId,
    ...(options?.correlationId !== undefined
      ? { correlation_id: options.correlationId }
      : {}),
  };
  const res = await fetch(`/api/dispatch/${actionName}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  let parsed: unknown;
  try {
    parsed = await res.json();
  } catch {
    return {
      ok: false,
      code: "non_json_response",
      message: `non-JSON response from /api/dispatch/${actionName} (HTTP ${res.status})`,
      request_id: requestId,
    };
  }
  if (
    parsed !== null &&
    typeof parsed === "object" &&
    !Array.isArray(parsed) &&
    (parsed as { ok?: unknown }).ok === true
  ) {
    const ok = parsed as {
      response: TOutput;
      idempotency_replay_short_circuit?: boolean;
    };
    return {
      ok: true,
      outcome: "ok",
      response: ok.response,
      ...(ok.idempotency_replay_short_circuit
        ? { idempotency_replay_short_circuit: true }
        : {}),
      request_id: requestId,
    };
  }
  const fail = (parsed ?? {}) as {
    code?: string;
    message?: string;
  };
  return {
    ok: false,
    code: typeof fail.code === "string" ? fail.code : "unknown_error",
    message:
      typeof fail.message === "string"
        ? fail.message
        : `HTTP ${res.status} returned no message`,
    request_id: requestId,
  };
}
